import { IaDrawer } from "ia-drawer";
import { EVENTS, publish, subscribe } from "ia-util-pubsub";
import IaCartAPI, { shoppableCount, holdsBagOpen, cartHeld, hasPurchasedLine, refusalContext } from "ia-cart-api";
import { captureError } from "ia-error-report";
import { bindCloseCursor } from "ia-close-cursor";
import { whenSwym } from "ia-standard-events";
// The drawer's accordion rows live inside data-re-render blocks, and a script
// node moved in by updateBlocks never executes; imported here, the accordion
// is defined before any code that can open the drawer runs.
import "ia-accordion";

/* The drawer's two timeouts, both last resorts. Change them here. */

// How long a checkout submit may be held while the cart settles. Past it the
// submit goes out anyway (and reports): checkout is never blocked for good.
const CHECKOUT_WAIT_MS = 4000;

// How long the row spinners stay up when no answer arrives at all. A cart
// write is a section render on top of the mutation; on a phone that round
// trip alone runs to several seconds.
const PRELOAD_FALLBACK_MS = 6000;
// A write still unanswered this long after the tap is reported; a slow phone
// answers between the two, with the spinners already lifted.
const WRITE_UNANSWERED_MS = 15000;

// How long an auto-open (?cart=open) may wait for the drawer's async styles.
// The merchant's value rides data-open-wait; this covers markup without it.
const OPEN_DRESSED_WAIT_MS = 600;

/** Editor moments that mean the merchant is working on this section. */
const EDITOR_EVENTS = ["shopify:section:load", "shopify:section:select", "shopify:block:select"];

class IaCart extends IaDrawer {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback?.();
    this.openFromUrl();
    // Paired with disconnectedCallback: the drawer's teleport reruns this, and
    // unpaired subscribes doubled every handler.
    this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
    this.unsubscribers = [
      subscribe(EVENTS.cartBeforeChange, this.preload.bind(this)),
      subscribe(EVENTS.cartChanged, this.preloadDone.bind(this)),
      subscribe(EVENTS.cartChanged, this.updateBlocks.bind(this)),
      // After updateBlocks on purpose: it re-toggles classes on the container
      // updateBlocks just replaced.
      subscribe(EVENTS.cartChanged, this.syncEmptyTruth.bind(this)),
      subscribe(EVENTS.cartChanged, this.updateFooter.bind(this)),
      subscribe(EVENTS.cartChanged, this.announceCartChange.bind(this)),
      subscribe(EVENTS.cartQtyClamped, this.showClampMessage.bind(this)),
      // A failed write repaints nothing, so without this the spinner would sit
      // there until the 3s fallback timer.
      subscribe(EVENTS.cartError, this.preloadDone.bind(this)),
    ];
    // Once per page, not per teleport: the mirrors do not change mid-session.
    if (!this.iaBadgeMirrorChecked) {
      this.iaBadgeMirrorChecked = true;
      this.checkBadgeMirror();
    }
    // The shared tracked X over the overlay (ia-close-cursor); the edge is the
    // whole visible assembly, so the seam by the panel keeps a normal pointer.
    bindCloseCursor(this, this.overlay, () => this.assemblyLeft());
    // One binding for the drawer's lifetime (the footer is never re-rendered).
    // Flag-guarded: a second guard would catch the first one's release submit
    // and hold it forever.
    const cartForm = this.querySelector("#ia-cart-form");
    if (cartForm && !cartForm.dataset.iaSubmitGuard) {
      cartForm.dataset.iaSubmitGuard = "1";
      cartForm.addEventListener("submit", this.guardSubmit.bind(this));
    }
    this.bindEditor();
  }

  /**
   * The theme editor renders this section again on every setting change, and
   * the drawer that carried the setting goes down with it. Selecting the
   * section or any block inside it opens the drawer, and the re-render that
   * follows a change opens it back, so the merchant keeps editing what they
   * are looking at. Editor only; the storefront never opens the bag by itself.
   */
  bindEditor() {
    if (!window.Shopify?.designMode) return;
    // remove-then-add, like the drawer's outward listeners: the teleport reruns
    // connectedCallback and a plain add would stack a second handler.
    if (this.onEditorEvent) {
      EDITOR_EVENTS.forEach((name) => document.removeEventListener(name, this.onEditorEvent));
    }
    this.onEditorEvent = (event) => {
      if (String(event.detail?.sectionId) !== this.dataset.sectionId) return;
      this.open();
    };
    EDITOR_EVENTS.forEach((name) => document.addEventListener(name, this.onEditorEvent));
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.unsubscribers?.forEach((unsubscribe) => unsubscribe());
    this.unsubscribers = null;
    if (this.onEditorEvent) {
      EDITOR_EVENTS.forEach((name) => document.removeEventListener(name, this.onEditorEvent));
      this.onEditorEvent = null;
    }
  }

  /**
   * A bag with lines but not one purchase reaching checkout is a catalog or
   * campaign hole (a hand-added $0 product, a stranded gift), not the theme's
   * to block: reported once per page, always let through.
   */
  checkFreeCheckout() {
    const cart = window.iaCartAPI?.cart;
    if (!cart?.items?.length || hasPurchasedLine(cart)) return false;
    if (!this.reportedFreeCheckout) {
      this.reportedFreeCheckout = true;
      const free = new Error("checkout submitted with no purchased line in the bag");
      console.error("[ia-cart]", free.message);
      captureError(free, {
        // A bag of gift cards and rewards only is a real checkout, so this
        // reads as a pattern over days, not as breakage now.
        level: "warning",
        where: "cart:free-checkout",
        types: cart.items.map((item) => item.product_type || "untyped").join(","),
        count: cart.items.length,
      });
    }
    return true;
  }

  /**
   * Holds a checkout submit while the cart is unsettled, or the order that opens
   * is not the one on screen: a write in flight, or a swap between its two
   * writes, where the bag holds both the incoming and the outgoing line. NOT a
   * disabled button: nothing here needs undoing, and every failure mode ends in
   * "checkout proceeds" (4s cap). Modern Narvar never submits this form.
   */
  guardSubmit(event) {
    this.checkFreeCheckout();
    this.checkRewardLines();
    // Our release re-enters here; the flag lets it through even at the 4s cap.
    if (this.releasingSubmit) {
      this.releasingSubmit = false;
      return;
    }
    // Once per shopper intent (begin_checkout in ia-ga4-events): a second tap
    // while the first is held is the same checkout.
    if (!this.pendingSubmit) publish(EVENTS.cartCheckout, { detail: { cartObj: window.iaCartAPI?.cart } });
    if (!this.unsettled) return;
    event.preventDefault();
    // A second click must not start a second wait, nor fall through.
    if (this.pendingSubmit) return;
    this.pendingSubmit = true;
    const form = event.currentTarget;
    // The button that was tapped: only the submitter's name reaches the POST,
    // and without `checkout` Shopify reads the form as a cart update.
    const submitter = event.submitter && form.contains(event.submitter) ? event.submitter : null;
    const startedAt = Date.now();
    // A timeout loop: rAF freezes in a hidden tab, and a submit must not.
    const tick = () => {
      if (!this.isConnected) return;
      const capped = Date.now() - startedAt > CHECKOUT_WAIT_MS;
      if (!this.unsettled || capped) {
        if (capped && this.unsettled) {
          // Checkout is never blocked for good, so this one goes out unsettled:
          // the order that opens may hold both halves of a swap.
          const late = new Error("checkout released before the cart settled");
          console.error("[ia-cart]", late.message);
          // The release is the design (checkout is never held for good), so a
          // pattern to read, not a page: `held` names what was still in flight.
          captureError(late, { where: "cart:checkout-unsettled", level: "warning", held: String(cartHeld()) });
        }
        this.pendingSubmit = false;
        this.releasingSubmit = true;
        if (submitter) form.requestSubmit(submitter);
        else form.requestSubmit();
      } else {
        setTimeout(tick, 80);
      }
    };
    setTimeout(tick, 80);
  }

  /**
   * The badge is two answers to one question: ia-cart-badge-count (Liquid) and
   * shoppableCount (JS), fed the same settings. A drift means the number jumps
   * on the first cart change, to whichever side is wrong. Compared once per
   * load against the seeded cart Liquid rendered from.
   */
  checkBadgeMirror() {
    let seeded = null;
    try {
      seeded = JSON.parse(document.getElementById("ia-cart-json")?.textContent);
    } catch {
      return;
    }
    const rendered = parseInt(this.querySelector(".ia-cart__header-count")?.textContent, 10);
    if (!seeded?.items || Number.isNaN(rendered)) return;
    const mine = shoppableCount(seeded);
    if (mine === rendered) return;
    const drift = new Error("the Liquid and JS bag badges disagree on one cart");
    console.error("[ia-cart]", drift.message, { liquid: rendered, js: mine });
    captureError(drift, { where: "cart:badge-mirror", liquid: rendered, js: mine });
  }

  /**
   * A Passport Reward line is the loyalty app's to add and to zero. One with a
   * price on it at checkout means a direct /cart/add (the drawer says FREE,
   * checkout will charge) or the app's discount failing: either way the team
   * must see it. Read at the checkout tap, not on every cart change: a price
   * on the line matters for the bag that goes to checkout, not for every
   * state the bag passes through. Never removed here: the app's marker is not
   * ours to guess.
   */
  checkRewardLines() {
    // Once per variant per page: a held checkout re-enters guardSubmit.
    this.shoutedRewards ||= new Set();
    (window.iaCartAPI?.cart?.items || []).forEach((item) => {
      if (item.product_type !== "Passport Reward" || item.final_price === 0) return;
      if (this.shoutedRewards.has(item.variant_id)) return;
      this.shoutedRewards.add(item.variant_id);
      const priced = new Error(
        `reward ${item.variant_id} is priced in the cart, checkout will charge it`
      );
      console.error("[ia-cart]", priced.message, item.variant_id);
      captureError(priced, {
        where: "reward:priced",
        variant: String(item.variant_id),
        price: item.final_price,
      });
    });
  }

  /** A write in flight, or a swap holding the cart between its two writes. */
  get unsettled() {
    return Boolean(this.preloading) || cartHeld();
  }

  /**
   * The region lives outside every `data-re-render` block: replace the node an
   * assistive technology watches and it announces once, then watches a detached
   * element forever.
   */
  announce(text) {
    const region = this.querySelector("[data-cart-live-region]");
    if (!region || !text) return;
    // Clearing first makes an identical message announce again; a timeout and
    // not rAF, which does not run in a hidden tab.
    region.textContent = "";
    clearTimeout(this.announceTimer);
    this.announceTimer = setTimeout(() => {
      region.textContent = text;
    }, 60);
  }

  announceCartChange(event) {
    if (!event.detail?.cartObj?.items) return;
    const count = shoppableCount(event.detail.cartObj);
    const template = this.querySelector("[data-cart-live-region]")?.dataset.updatedText;
    if (template) this.announce(template.replace("[count]", count));
  }

  /**
   * The server-clamp explanation, on the line it happened to. Runs after
   * updateBlocks, so it writes into freshly rendered markup.
   */
  showClampMessage(event) {
    const { key, actual, message } = event.detail || {};
    const slot = this.querySelector(`ia-cart-item[data-key="${key}"] [data-line-message]`);
    if (!slot) return;
    // Two shapes: a silent 200 (compose from the template) and a 422 that
    // carries the server's own sentence, used verbatim.
    const text = message
      || (slot.dataset.clampText || "Only [count] available").replace("[count]", actual);
    slot.textContent = text;
    slot.hidden = false;
    this.announce(text);
  }

  /**
   * Refreshes the footer WITHOUT replacing it: swapping it destroys the checkout
   * form and the app mounts inside, and Narvar's buttons hold the reference they
   * resolved at render. Apps observing the total need its node identity too.
   *
   * Values are copied from the fresh markup, so formatting stays in Liquid.
   */
  updateFooter(event) {
    const source = event.detail?.cartDom?.querySelector("#ia-cart-footer");
    const target = this.querySelector("#ia-cart-footer");
    if (!source || !target) return;

    target.classList.toggle("ia-hidden", source.classList.contains("ia-hidden"));

    const totalFrom = source.querySelector(".ia-cart-footer__total-value");
    const totalTo = target.querySelector(".ia-cart-footer__total-value");
    if (totalFrom && totalTo) totalTo.textContent = totalFrom.textContent.trim();

    const labelFrom = source.querySelector("[data-checkout-button]")?.getAttribute("aria-label");
    const button = target.querySelector("[data-checkout-button]");
    if (labelFrom && button) button.setAttribute("aria-label", labelFrom);
  }

  /**
   * /cart redirects to the home page with ?cart=open. The flag is dropped after,
   * so a refresh or a shared link does not keep reopening the drawer.
   */
  openFromUrl() {
    const url = new URL(window.location.href);
    if (url.searchParams.get("cart") !== "open") return;
    url.searchParams.delete("cart");
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    // Not this.open() directly: the blocks' styles load async, and an instant
    // open showed the tiers undressed for a beat (seen returning from
    // checkout). Waits for the styles still in flight, capped so a stalled
    // sheet never holds the bag shut; a warm cache opens immediately.
    const wait = this.dataset.openWait === undefined
      ? OPEN_DRESSED_WAIT_MS
      : parseInt(this.dataset.openWait, 10) || 0;
    const pending = [
      ...document.querySelectorAll('link[rel="stylesheet"], link[rel="preload"][as="style"]'),
    ].filter((link) => !link.sheet);
    if (!wait || !pending.length) {
      this.open();
      return;
    }
    const dressed = Promise.all(
      pending.map(
        (link) =>
          new Promise((resolve) => {
            // the sheet may have landed between the snapshot above and this
            // subscription: re-checking closes the missed-event gap, and the
            // cap below still covers a load that never fires at all
            if (link.sheet) return resolve();
            link.addEventListener("load", resolve, { once: true });
            link.addEventListener("error", resolve, { once: true });
          })
      )
    );
    this.pendingAutoOpen = true;
    const cap = new Promise((resolve) => setTimeout(resolve, wait));
    Promise.race([dressed, cap]).then(() => {
      if (this.pendingAutoOpen) this.open();
    });
  }

  get preloading() {
    return this.hasAttribute("data-preloading");
  }

  set preloading(value) {
    this.toggleAttribute("data-preloading", value);
  }

  preload(e) {
    // Before the drawer-level guard below: a second line changed mid-write would
    // otherwise stay dark while its price moved.
    const markLoading = (key, qty) => {
      const line = this.querySelector(`ia-cart-item[data-key="${key}"]`);
      if (!line) return;
      line.classList.add("is-loading");
      // A line on its way to zero leaves on the tap, not after both round trips.
      // Presentation only: the next render, or preloadDone, puts it back.
      if (qty === 0) line.classList.add("is-leaving");
    };
    if (e?.detail?.updates) {
      Object.entries(e.detail.updates).forEach(([key, qty]) => markLoading(key, qty));
      this.previewEmpty(e.detail.updates);
    }
    if (e?.detail?.changes?.id) markLoading(e.detail.changes.id);

    // Restarted per write: a long burst would hit the cap mid-flight. The
    // spinners are lifted so the drawer stays usable, with the state on screen
    // whatever the last answer said; the write itself may still land.
    clearTimeout(this.preloadFallback);
    this.preloadFallback = setTimeout(() => {
      if (this.preloading) this.liftSpinners();
    }, PRELOAD_FALLBACK_MS);
    clearTimeout(this.unansweredReport);
    this.unansweredReport = setTimeout(() => {
      const stuck = new Error("a cart write never answered");
      console.error("[ia-cart]", stuck.message);
      captureError(stuck, { where: "cart:preload-stuck", level: "warning", noise: true });
    }, WRITE_UNANSWERED_MS);

    if (this.preloading) return;
    this.preloading = true;
    this.classList.add("loading");
  }

  /**
   * Says "empty" before the server does: the title and button ship on every
   * render, so it is a class away. Prediction only, never the last word.
   */
  previewEmpty(updates) {
    const api = window.iaCartAPI;
    if (!api?.cart) return;
    // holdsBagOpen, not shoppableCount: a granted line leaves with the last
    // purchase, so it must not hold the empty state back.
    const empty = !api.predictedLines(updates).some(holdsBagOpen);
    // Remembered so syncEmptyTruth can report a prediction the server refuted.
    this.emptyPredicted = empty;
    this.querySelector("#ia-cart-items")?.classList.toggle("is-empty", empty);
    this.toggleAttribute("data-bag-empty", empty);
  }

  /**
   * The server's answer over previewEmpty's guess, and the report when the two
   * disagree. Also HEALS the attribute: nothing else ever cleared it, so adding
   * to an emptied bag left the drawer footerless until a reload.
   */
  syncEmptyTruth(event) {
    const cartObj = event.detail?.cartObj;
    if (!cartObj?.items) return;
    const empty = !cartObj.items.some(holdsBagOpen);
    const predicted = this.emptyPredicted;
    this.emptyPredicted = undefined;
    if (predicted !== undefined && predicted !== empty) {
      const error = new Error(
        `predicted the bag ${predicted ? "empty" : "held open"}, the server disagreed`
      );
      console.error("[ia-cart]", error.message);
      captureError(error, {
        where: "cart:predicted-empty",
        // The server's answer is already on screen: a prediction to read in a
        // batch, not a page to go and look at.
        level: "warning",
        predicted: String(predicted),
        lines: cartObj.items.length,
      });
    }
    this.toggleAttribute("data-bag-empty", empty);
    this.querySelector("#ia-cart-items")?.classList.toggle("is-empty", empty);
  }

  preloadDone(event) {
    clearTimeout(this.preloadFallback);
    clearTimeout(this.unansweredReport);
    this.liftSpinners();
  }

  liftSpinners() {
    this.preloading = false;
    this.classList.remove("loading");
    // Explicit, not left to the markup swap: a failed write repaints nothing.
    this.querySelectorAll("ia-cart-item.is-loading, ia-cart-item.is-leaving").forEach((item) => {
      item.classList.remove("is-loading", "is-leaving");
    });
  }

  /**
   * Swaps each re-rendered block for the freshly rendered one.
   *
   * `data-preserve-content` ELEMENTS are moved across the swap, not just their
   * children: a fresh container kills the listeners an app bound to it. Nodes
   * are moved and replaced as nodes, never through outerHTML, which drops every
   * listener.
   */
  updateBlocks(e) {
    const html = e.detail?.cartDom;
    if (!html) return;

    this.querySelectorAll("[data-re-render]").forEach((block) => {
      if (!block.id) return;
      const source = html.querySelector(`#${block.id}`);
      if (!source) return;

      const fresh = document.importNode(source, true);

      // A line the fresh markup agrees with is MOVED across instead of rebuilt.
      // One tap costs two writes (the add, then the invisible default's
      // removal), and a rebuilt row re-creates its <img>: the whole list blinked
      // on an answer that changed nothing the shopper can see. Agreement is
      // data-sig, stamped by Liquid with everything that can change the row's
      // markup, so JS never has to diff the markup itself.
      const twins = new Map();
      fresh.querySelectorAll("ia-cart-item[data-key]").forEach((twin) => {
        twins.set(twin.dataset.key, twin);
      });
      block.querySelectorAll("ia-cart-item[data-key]").forEach((mounted) => {
        const twin = twins.get(mounted.dataset.key);
        if (!twin || !twin.dataset.sig || twin.dataset.sig !== mounted.dataset.sig) return;
        // Two runtime states mean the mounted row is no longer what its sig
        // says: a typed quantity the server has not confirmed, and a clamp
        // message (documented to clear on the next render).
        const typed = mounted.querySelector(".ia-product-qty__input");
        const rendered = twin.querySelector(".ia-product-qty__input");
        if (typed && rendered && String(typed.value) !== rendered.getAttribute("value")) return;
        if (mounted.querySelector("[data-line-message]")?.textContent.trim()) return;
        twin.replaceWith(mounted);
      });

      // What the doomed rows leave behind: an <img> of the same file, already
      // decoded. A rebuilt row's new <img> on a different CDN width is a
      // fresh load, i.e. a blank frame.
      // Line images only, both sides: unscoped, the picker's thumb sits first
      // in document order and takes the entry meant for the line.
      const loaded = new Map();
      block.querySelectorAll("ia-cart-item img[src]").forEach((image) => {
        if (!image.complete || !image.naturalWidth) return;
        const file = new URL(image.currentSrc || image.src, window.location.href).pathname;
        if (!loaded.has(file)) loaded.set(file, image);
      });
      fresh.querySelectorAll("ia-cart-item img[src]").forEach((image) => {
        const file = new URL(image.getAttribute("src"), window.location.href).pathname;
        const decoded = loaded.get(file);
        if (!decoded) return;
        loaded.delete(file);
        // The pixels come from the old node, everything the markup says from the new.
        ["alt", "class", "width", "height", "loading", "sizes"].forEach((name) => {
          const value = image.getAttribute(name);
          if (value === null) decoded.removeAttribute(name);
          else decoded.setAttribute(name, value);
        });
        image.replaceWith(decoded);
      });

      block.querySelectorAll("[data-preserve-content]").forEach((mounted) => {
        if (!mounted.id) return;
        const slot = fresh.querySelector(`#${mounted.id}`);
        if (slot) slot.replaceWith(mounted);
      });

      // The server renders rows collapsed, so a cart change folded whatever was
      // open. Matched by position: the rows have no ids, the order is stable.
      const freshRows = fresh.querySelectorAll("ia-accordion-component");
      block.querySelectorAll("ia-accordion-component").forEach((row, index) => {
        if (!row.classList.contains("ia-accordion--expanded")) return;
        const twin = freshRows[index];
        if (!twin) return;
        twin.classList.add("ia-accordion--expanded");
        twin.querySelector("[data-toggle-accordion]")?.setAttribute("aria-expanded", "true");
      });

      block.replaceWith(fresh);
    });
  }

  open() {
    // Any real open or close outranks a queued auto-open: the shopper who
    // already touched the drawer while the styles loaded has answered.
    this.pendingAutoOpen = false;
    super.open();
    document.body.classList.add("is-cart-open");
  }

  close() {
    this.pendingAutoOpen = false;
    super.close();
    document.body.classList.remove("is-cart-open");
  }

  /** Left edge of the visible drawer assembly: the window and, when shown, the panel beside it. */
  assemblyLeft() {
    const parts = [this.window, ...this.querySelectorAll(".ia-cart__upsell-section")]
      .filter((el) => el && el.offsetWidth > 0);
    return Math.min(...parts.map((el) => el.getBoundingClientRect().left));
  }
}

class IaCartItem extends HTMLElement {
  connectedCallback() {
    this.key = this.dataset.key;
    this.ts = this.dataset.ts || null;
    this.removeButton = this.querySelector(".js-cart-item-remove");
    this.qtyPicker = this.querySelector("ia-product-qty");
    if (this.qtyPicker) {
      this.unsubscribeQtyChange = subscribe(
        EVENTS.qtyChange,
        this.handleQtyChange.bind(this)
      );
    }

    // same reconnect guard as the form submit above: the drawer's move to
    // <body> reruns connectedCallback and would stack a second click listener
    if (this.removeButton && !this.removeButton.dataset.iaRemoveBound) {
      this.removeButton.dataset.iaRemoveBound = "1";
      this.removeButton.addEventListener("click", this.handleRemove.bind(this));
    }
  }

  disconnectedCallback() {
    this.unsubscribeQtyChange?.();
  }

  handleQtyChange(event) {
    const { value, id } = event.detail;
    if (id !== this.key) return;

    // A bundle's stepper scales every line by the parent's factor, in ONE write.
    const bundleKeys = this.dataset.bundleKeys?.split(",").filter(Boolean);
    if (bundleKeys?.length) {
      const api = window.iaCartAPI;
      const parentQty = parseInt(this.dataset.qty, 10) || 1;
      if (!api?.cart || value === parentQty) return;
      const updates = {};
      bundleKeys.forEach((key) => {
        const line = api.cart.items.find((item) => item.key === key);
        if (line) updates[key] = Math.max(0, Math.round((line.quantity / parentQty) * value));
      });
      api.updateCart({ data: { updates } }).catch(() => {});
      return;
    }

    // failure already surfaced via cartError + the line message; the rethrow
    // here would only be an unhandled rejection in the console
    window.iaCartAPI?.updateQty({ key: this.key, qty: value, _ts: this.ts })?.catch(() => {});
  }

  /**
   * Inline styles, not a class: the height has to be measured anyway, and mixing
   * an inline height with class-driven padding split them across the cascade.
   */
  collapse() {
    const { height } = this.getBoundingClientRect();
    const motionOk = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    Object.assign(this.style, {
      height: `${height}px`,
      overflow: "hidden",
      pointerEvents: "none",
      transition: motionOk ? "height .25s ease, padding .25s ease, opacity .25s ease" : "none",
    });
    this.getBoundingClientRect();
    Object.assign(this.style, {
      height: "0px",
      paddingTop: "0px",
      paddingBottom: "0px",
      marginBottom: "0px",
      borderTopWidth: "0px",
      borderBottomWidth: "0px",
      opacity: "0",
    });
  }

  restore() {
    this.classList.remove("is-removing");
    this.style.cssText = "";
  }

  async handleRemove() {
    if (this.classList.contains("is-removing")) return;
    this.classList.add("is-removing");
    this.collapse();

    // Bundle card: zero every line of the group in one update.
    const bundleKeys = this.dataset.bundleKeys?.split(",").filter(Boolean);
    const write = bundleKeys?.length
      ? window.iaCartAPI?.updateCart({
          data: { updates: Object.fromEntries(bundleKeys.map((key) => [key, 0])) },
        })
      : window.iaCartAPI?.updateQty({ key: this.key, qty: 0, _ts: this.ts });

    try {
      await write;
    } catch (error) {
      // Put it back: a line that vanished while still in the cart is worse
      // than a slow one.
      this.restore();
      captureError(error, { ...refusalContext(error, "cart:remove"), key: String(this.key) });
    }
  }
}

/**
 * Save for later: the line is MOVED into Swym's SFL list (a separate list
 * from the wishlist). Swym stores it; the click, the button and the removal
 * are the theme's. We remove on Swym's CONFIRM, so a failed save leaves the
 * bag whole; a silent Swym wakes the button back up and reports.
 */
const SFL_WAIT_MS = 6000;
let sflListId = null;

/**
 * Swym installs SFL twice: raw on `swat` (what their docs use) and an
 * instrumented copy on `swat.api` (what prod shows). Prefer the instrumented
 * one so their analytics count the save; `swat.api.SaveForLater` also exists
 * as an EMPTY object before the module installs, hence the method check.
 */
const sflApi = (swat) => {
  if (swat?.api?.SaveForLater?.add) return swat.api.SaveForLater;
  if (swat?.SaveForLater?.add) return swat.SaveForLater;
  return null;
};

/**
 * Whether Swym ever turned up, decided once per page: the answer cannot change
 * mid-session, and every line asks the same question.
 */
let sflAvailable = null;

class IaCartSfl extends HTMLElement {
  connectedCallback() {
    // reconnect guard: the drawer teleports to <body> and reruns this
    this.button = this.querySelector("button");
    this.line = this.closest("ia-cart-item");
    if (this.button && !this.button.dataset.iaSflBound) {
      this.button.dataset.iaSflBound = "1";
      this.button.addEventListener("click", this.save.bind(this));
    }
    this.hideWithoutSwym();
  }

  /**
   * No merchant switch for this button: whether the wishlist app is installed
   * is a fact only JS can read. Visible by default (installed is the normal
   * case, and hiding first would make it pop in), removed for good the moment
   * Swym proves absent. Not reported: whether a third-party script arrived is
   * the app's news, not the theme's.
   */
  hideWithoutSwym() {
    if (sflAvailable === false) {
      this.hidden = true;
      return;
    }
    if (sflAvailable === true || !this.isConnected) return;
    // whenSwym, never a bare SwymCallbacks push: the app drains that queue on
    // load, so a push after it lands is never called and an app that arrived
    // early reads as absent.
    whenSwym((swat) => {
      sflAvailable = Boolean(swat);
      if (sflAvailable) return;
      document.querySelectorAll("ia-cart-sfl").forEach((el) => { el.hidden = true; });
    });
    // Swym never calls back at all when its script is blocked or absent.
    setTimeout(() => {
      if (sflAvailable !== null) return;
      sflAvailable = false;
      document.querySelectorAll("ia-cart-sfl").forEach((el) => { el.hidden = true; });
    }, SFL_WAIT_MS);
  }

  save() {
    const { key, variantId, productId, productUrl, title, price, image, bundleParts } = this.button.dataset;
    // The optimistic paint: the line dims on the tap and stops taking input
    // (one writer per line). Confirmed = it leaves; failed = it wakes back.
    this.button.disabled = true;
    this.line?.toggleAttribute("data-sfl-saving", true);
    let answered = false;
    const settle = (fn) => {
      if (answered) return;
      answered = true;
      clearTimeout(wake);
      fn();
    };
    const revive = () => {
      this.button.disabled = false;
      this.line?.removeAttribute("data-sfl-saving");
    };
    const wake = setTimeout(() => settle(() => {
      revive();
      const silent = new Error("save for later got no answer from the wishlist app");
      console.error("[ia-cart]", silent.message);
      // The line stays in the bag and the button wakes: the app's silence is
      // its own to answer for.
      captureError(silent, { where: "cart:sfl", level: "warning", noise: true, variant: String(variantId) });
    }), SFL_WAIT_MS);

    const fail = (error) => settle(() => {
      revive();
      const failed = error instanceof Error ? error : new Error("save for later failed");
      console.error("[ia-cart] save for later failed", error);
      captureError(failed, { where: "cart:sfl", level: "warning", variant: String(variantId) });
    });

    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push((swat) => {
      const run = () => this.moveToList(swat, { key, variantId, productId, productUrl, title, price, image, bundleParts }, settle, fail);
      if (sflApi(swat)) {
        run();
        return;
      }
      // The SFL API lives in Swym's UI bundle, and the new-gen embed is a
      // ghost shell that fetches it on first use: load it the way the SDK
      // itself does, once. Loading is not enough on its own — the bundle
      // installs the API only when the store's `retailerSettings.SFL
      // .SFLFeatureEnabled` is true, which is Swym support's switch, not an
      // admin toggle (verified against the production store, which has it).
      const assets = window.getSwymConfig?.()?.assets;
      if (window.SwymUtils?.loadUIBundle && assets) {
        const file = window.SwymShopifyCdnInUse && assets.includes("cdn.shopify")
          ? "apps.bundle.js"
          : "v3/apps.bundle.js";
        const url = window.__SwymUIBundlePath || assets + file;
        window.SwymUtils.loadUIBundle(() => {
          // a beat for the bundle to register its APIs before the retry
          setTimeout(() => {
            if (sflApi(swat)) run();
            else fail(new Error("the Swym SaveForLater module never attached"));
          }, 80);
        }, `${url}?v=${window.__SWYM__VERSION__ || "0"}`);
        return;
      }
      fail(new Error("the Swym SaveForLater module never attached"));
    });
  }

  /** The move itself: into the dedicated SFL list, removal on Swym's confirm. */
  moveToList(swat, { key, variantId, productId, productUrl, title, price, image, bundleParts }, settle, fail) {
    const sfl = sflApi(swat);
    // Swym's own field for data it does not model: the wishlist page reads it
    // back off the card and rebuilds the group from it. Its cap is 800 chars of
    // JSON, which a set of parts is nowhere near.
    const cprops = bundleParts ? { iaBundleParts: bundleParts } : undefined;
    const add = (lid) => sfl.add(
      lid,
      [{
        // Swym's field names, seen live in SwymPageData: empi = product id,
        // epi = variant id, du/dt/iu = url/title/image, pr in currency
        // units. The image goes absolute: Swym serves it from its own
        // pages, where a relative path points at the wrong host.
        empi: Number(productId),
        epi: Number(variantId),
        du: productUrl,
        dt: title || "",
        iu: image ? new URL(image, productUrl).href : "",
        pr: Number(price) || 0,
        qty: 1,
        ...(cprops ? { cprops } : {}),
      }],
      () => settle(() => {
        // The dim rides until the removal lands; a failed write revives the
        // row (updateBlocks reuses nodes by sig, so it would stay dim). A
        // bundle leaves as one group, the way its X removes it.
        const bundleKeys = this.line?.dataset.bundleKeys?.split(",").filter(Boolean);
        const removal = bundleKeys?.length
          ? window.iaCartAPI?.updateCart({
              data: { updates: Object.fromEntries(bundleKeys.map((k) => [k, 0])) },
            })
          : window.iaCartAPI?.updateQty({ key, qty: 0 });
        removal?.finally?.(() => this.line?.removeAttribute("data-sfl-saving"));
      }),
      fail
    );
    if (sflListId) {
      add(sflListId);
      return;
    }
    sfl.init((list) => {
      sflListId = list.listid;
      add(sflListId);
    }, fail);
  }
}

class IaCartPlanPicker extends HTMLElement {
  connectedCallback() {
    this.key = this.dataset.key;

    // reconnect guard (drawer teleports to <body>): a second set of listeners
    // here sent /cart/change.js twice per plan change
    if (this.iaBound) return;
    this.iaBound = true;

    if (this.hasAttribute("data-add-plan")) {
      this.addEventListener("click", this.handleAddPlan.bind(this));
    }

    const planSelect = this.querySelector("[data-plan-select]");
    if (planSelect) {
      planSelect.addEventListener("change", this.handlePlanChange.bind(this));
    }
  }

  getQuantity() {
    const cartItem = this.closest("ia-cart-item");
    return parseInt(cartItem?.dataset.qty || 1, 10);
  }

  async handleAddPlan(event) {
    event.preventDefault();
    const planId = this.dataset.addPlan;
    if (planId && this.key) {
      await window.iaCartAPI?.changePlan({ 
        id: this.key, 
        quantity: this.getQuantity(), 
        planId 
      });
    }
  }

  async handlePlanChange(event) {
    const planId = event.target.value === "one-time" ? null : event.target.value;
    if (this.key) {
      await window.iaCartAPI?.changePlan({ 
        id: this.key, 
        quantity: this.getQuantity(), 
        planId 
      });
    }
  }
}

customElements.define("ia-cart", IaCart);
customElements.define("ia-cart-item", IaCartItem);
customElements.define("ia-cart-sfl", IaCartSfl);
customElements.define("ia-cart-plan-picker", IaCartPlanPicker);

